import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';
import type {
	IncludeCountProps,
	AccountInviteCreatePayload,
	GroupPayload,
	SettingsPayload,
	FetchSquadParams,
	UpdatePerformanceReviewTemplatePayload,
	UpdateGroupMemberPayload,
	UpdateGroupMemberParams,
	UpdateNumberAndPositionParams,
} from 'pkg/actions/groups/interface';

import * as endpoints from 'pkg/api/endpoints/auto';
import * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import {
	normalizeMembership,
} from 'pkg/api/models/membership/utils';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

export type {
	IncludeCountProps,
	AccountInviteCreatePayload,
	GroupPayload,
	SettingsPayload,
	FetchSquadParams,
	UpdatePerformanceReviewTemplatePayload,
	UpdateGroupMemberPayload,
	UpdateGroupMemberParams,
};

export const fetchGroup = async (
	organizationId: number,
	groupId: number,
	queryParams?: IncludeCountProps
): Promise<models.group.Group> => {
	const response = await sdk.get(
		endpoints.Groups.Show(organizationId, groupId),
		queryParams
	);

	if (!response.ok) {
		return null;
	}

	return response.json();
};

export async function update(
	organizationId: number,
	groupId: number,
	payload: GroupPayload
): Promise<void> {
	// It's impossible to set a group to a top group after it's created, to avoid potential issues with that we remove parentgroupId from the payload if it's null.
	if (
		payload.hasOwnProperty('parentGroupId') &&
		payload.parentGroupId === null
	) {
		delete payload['parentGroupId'];
	}

	const response = await sdk.patch(
		endpoints.Groups.Update(organizationId, groupId),
		{},
		payload
	);

	if (!response.ok) {
		return Promise.reject(response);
	}
}

export async function updateSettings(
	organizationId: number,
	groupId: number,
	payload: SettingsPayload
): Promise<boolean> {
	const req = await sdk.patch(
		endpoints.Groups.UpdateSettings(organizationId, groupId),
		{},
		payload
	);

	if (!req.ok) {
		const err = crash();

		flashActions.show({
			title: err.title,
			message: err.description,
		});

		return false;
	}

	return true;
}

export const updatePerformanceReviewTemplate = async (
	organizationId: number,
	{ groupId, template }: UpdatePerformanceReviewTemplatePayload
) => {
	const req = await sdk.put(
		endpoints.Groups.SetPerformanceReviewTemplate(organizationId, groupId),
		{},
		{
			template,
		}
	);

	if (!req.ok) {
		flashActions.show({
			title: t(`Save failed`),
			message: t(`Failed to save, make sure you have written something.`),
		});
		return false;
	}

	flashActions.show(
		{
			title: t(`Template saved`),
			message: t(`Template was saved successfully.`),
		},
		200
	);
	return true;
};

export async function createInvites(
	groupId: number,
	emails: Array<{
		targetEmail: string;
		language: string;
		role: models.membership.RoleType;
	}>
): Promise<void> {
	let numInvitesCreated: number = 0;

	for (const pendingInvite of Object.values(emails)) {
		const payload = {
			email: pendingInvite.targetEmail,
			languageCode: pendingInvite.language,
			targets: [
				{
					type: models.accountInvite.TargetType.Group,
					targetId: groupId,
					role: pendingInvite.role,
				},
			],
		};

		const [request] = await create<
			AccountInviteCreatePayload,
			models.accountInvite.AccountInvite
		>(endpoints.AccountInvites.Create(), payload);

		if (!request.ok) {
			continue;
		}

		numInvitesCreated = numInvitesCreated + 1;
	}

	if (numInvitesCreated > 0) {
		flashActions.show({
			title: t(`Users invited`),
			message: t(`Sent {num} invites`, {
				num: numInvitesCreated,
			}),
		});
	}
}

export async function fetchInvite(inviteId: number, inviteKey?: string) {
	const invite_key = inviteKey ? inviteKey : null;

	const request = await sdk.get(endpoints.AccountInvites.Show(inviteId), {
		invite_key,
	});
	if (!request.ok) {
		return;
	}

	const response = await request.json();
	return response;
}

export const updateGroupMember = async (
	organizationId: number,
	{ groupId, userId, payload }: UpdateGroupMemberParams
): Promise<[boolean, models.membership.Membership]> => {
	const req = await sdk.patch(
		endpoints.Groups.UpdateUser(organizationId, groupId, userId),
		{},
		payload
	);
	const resp = await req.json();

	if (!req.ok) {
		flashActions.show({ title: t('Something went wrong') }, req.status);
		return [false, resp];
	}

	return [true, normalizeMembership(resp)];
};

export async function updateNumberAndPosition(
	organizationId: number,
	{ groupId, userId, jerseyNumber, position }: UpdateNumberAndPositionParams
): Promise<[boolean, models.membership.Membership]> {
	let payload: object = { jerseyNumber };

	if (jerseyNumber === null) {
		payload = { clearJerseyNumber: true };
	}

	if (position === null) {
		payload = { ...payload, clearPosition: true };
	} else if (position !== undefined) {
		payload = { ...payload, position };
	}

	const req = await sdk.patch(
		endpoints.Groups.UpdateUser(organizationId, groupId, userId),
		{},
		payload
	);
	const resp = await req.json().catch(() => ({}));

	if (req.ok) {
		return [true, normalizeMembership(resp)];
	}

	if (resp?.code === 'jersey_number_taken') {
		flashActions.show({
			title: t('#{number} is already taken by {name}', {
				number: jerseyNumber,
				name: resp.name,
			}),
			message: t('Remove their number first, then try again.'),
		});
	} else {
		flashActions.show({ title: t('Something went wrong') }, req.status);
	}

	return [false, resp];
}

export async function createGroup(
	organizationId: number,
	payload: Partial<models.group.Group>
): Promise<boolean> {
	const [request] = await create<
		Partial<models.group.Group>,
		models.group.Group
	>(endpoints.Groups.Create(organizationId), payload);

	if (!request.ok) {
		return false;
	}

	return true;
}

export const deleteGroup = async (
	organizationId: number,
	groupId: number
): Promise<boolean> => {
	const req = await sdk.destroy(
		endpoints.Groups.Delete(organizationId, groupId)
	);
	if (!req.ok) {
		return false;
	}

	return true;
};

export const connectToStripe = async (
	organizationId: number
): Promise<void> => {
	const req = await sdk.post(
		endpoints.Stripe.CreateConnectAccountLink(organizationId),
		{},
		{
			returnUrl: location.href,
		}
	);

	if (req.ok) {
		const result = await req.json();
		location.href = result.redirect_url;
	} else {
		flashActions.show({
			title: t('Something went wrong'),
			message: t(
				"If you think this error isn't supposed to happen, please contact 360Player support."
			),
		});
	}
};

export async function createCalendarSubscriptionToken(
	accountId: number
): Promise<string> {
	const [request, response] = await create<
		null,
		models.accountToken.AccountToken
	>(endpoints.AccountTokens.CreateCalendarSubscription(accountId), null);

	if (!request.ok) {
		return '';
	}

	if (response.token) {
		return response.token;
	}

	return '';
}
